export interface WordSensorConfig {
  words?: string[];
  maskChar?: string;
  caseInsensitive?: boolean;
  logDetections?: boolean;
  enableRegex?: boolean;
  wordBoundary?: boolean;
  customReplacer?: (word: string, context: string) => string;
}

export interface DetectionStats {
  totalDetections: number;
  uniqueWords: string[];
  detectionCounts: Record<string, number>;
  lastDetectionTime?: Date;
}

export class WordSensor {
  private forbiddenWords: Map<string, string | null>;
  private regexPatterns: Map<string, RegExp>;
  private maskChar: string;
  private caseInsensitive: boolean;
  private logDetections: boolean;
  private enableRegex: boolean;
  private wordBoundary: boolean;
  private customReplacer?: (word: string, context: string) => string;
  private detectionLogs: string[];
  private detectionStats: DetectionStats;

  constructor(config: WordSensorConfig = {}) {
    const {
      words = [],
      maskChar = "*",
      caseInsensitive = true,
      logDetections = false,
      enableRegex = false,
      wordBoundary = true,
      customReplacer
    } = config;

    this.forbiddenWords = new Map();
    this.regexPatterns = new Map();
    this.maskChar = maskChar;
    this.caseInsensitive = caseInsensitive;
    this.logDetections = logDetections;
    this.enableRegex = enableRegex;
    this.wordBoundary = wordBoundary;
    this.customReplacer = customReplacer;
    this.detectionLogs = [];
    this.detectionStats = {
      totalDetections: 0,
      uniqueWords: [],
      detectionCounts: {}
    };

    words.forEach((word) => this.addWord(word));
  }

  addWord(word: string, mask?: string) {
    const key = this.caseInsensitive ? word.toLowerCase() : word;
    this.forbiddenWords.set(key, mask ?? null);
    
    if (this.enableRegex) {
      try {
        const pattern = this.wordBoundary ? `\\b${word.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b` : word;
        const flags = this.caseInsensitive ? "gi" : "g";
        this.regexPatterns.set(key, new RegExp(pattern, flags));
      } catch (error) {
        console.warn(`Invalid regex pattern for word "${word}":`, error);
      }
    }
  }

  addWords(words: string[]) {
    words.forEach((word) => this.addWord(word));
  }

  addRegexPattern(pattern: string, mask?: string) {
    if (!this.enableRegex) {
      throw new Error("Regex patterns are not enabled. Set enableRegex: true in config.");
    }
    
    try {
      const key = this.caseInsensitive ? pattern.toLowerCase() : pattern;
      this.forbiddenWords.set(key, mask ?? null);
      const flags = this.caseInsensitive ? "gi" : "g";
      this.regexPatterns.set(key, new RegExp(pattern, flags));
    } catch (error) {
      throw new Error(`Invalid regex pattern: ${error}`);
    }
  }

  removeWord(word: string) {
    const key = this.caseInsensitive ? word.toLowerCase() : word;
    this.forbiddenWords.delete(key);
    this.regexPatterns.delete(key);
  }

  removeWords(words: string[]) {
    words.forEach((word) => this.removeWord(word));
  }

  clearWords() {
    this.forbiddenWords.clear();
    this.regexPatterns.clear();
    this.resetStats();
  }

  getWords(): string[] {
    return Array.from(this.forbiddenWords.keys());
  }

  hasWord(word: string): boolean {
    const key = this.caseInsensitive ? word.toLowerCase() : word;
    return this.forbiddenWords.has(key);
  }

  private applyMask(word: string, maskType: "full" | "partial" | "smart"): string {
    if (maskType === "partial" && word.length > 2) {
      return word[0] + this.maskChar.repeat(word.length - 2) + word[word.length - 1];
    } else if (maskType === "smart") {
      // Smart masking: keep first and last character, mask middle
      if (word.length <= 2) return this.maskChar.repeat(word.length);
      return word[0] + this.maskChar.repeat(Math.max(1, Math.floor(word.length * 0.6))) + word[word.length - 1];
    }
    return this.maskChar.repeat(word.length);
  }

  private updateStats(word: string) {
    this.detectionStats.totalDetections++;
    this.detectionStats.lastDetectionTime = new Date();
    
    const key = this.caseInsensitive ? word.toLowerCase() : word;
    if (!this.detectionStats.uniqueWords.includes(key)) {
      this.detectionStats.uniqueWords.push(key);
    }
    
    this.detectionStats.detectionCounts[key] = (this.detectionStats.detectionCounts[key] || 0) + 1;
  }

  filter(
    text: string,
    mode: "replace" | "remove" | "highlight" = "replace",
    maskType: "full" | "partial" | "smart" = "full"
  ): string {
    if (this.forbiddenWords.size === 0) return text;

    if (this.enableRegex) {
      return this.filterWithRegex(text, mode, maskType);
    } else {
      return this.filterWithWords(text, mode, maskType);
    }
  }

  private filterWithWords(
    text: string,
    mode: "replace" | "remove" | "highlight",
    maskType: "full" | "partial" | "smart"
  ): string {
    const pattern = this.wordBoundary 
      ? `\\b(${[...this.forbiddenWords.keys()]
          .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
          .join("|")})\\b`
      : `(${[...this.forbiddenWords.keys()]
          .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
          .join("|")})`;

    const regex = new RegExp(pattern, this.caseInsensitive ? "gi" : "g");

    return text.replace(regex, (match) => {
      const key = this.caseInsensitive ? match.toLowerCase() : match;

      if (this.logDetections) {
        this.detectionLogs.push(match);
      }

      this.updateStats(match);

      if (mode === "remove") return "";
      if (mode === "highlight") return `[FILTERED: ${match}]`;

      if (this.customReplacer) {
        return this.customReplacer(match, text);
      }

      return this.forbiddenWords.get(key) ?? this.applyMask(match, maskType);
    });
  }

  private filterWithRegex(
    text: string,
    mode: "replace" | "remove" | "highlight",
    maskType: "full" | "partial" | "smart"
  ): string {
    let result = text;
    
    for (const [key, regex] of this.regexPatterns) {
      result = result.replace(regex, (match) => {
        if (this.logDetections) {
          this.detectionLogs.push(match);
        }

        this.updateStats(match);

        if (mode === "remove") return "";
        if (mode === "highlight") return `[FILTERED: ${match}]`;

        if (this.customReplacer) {
          return this.customReplacer(match, text);
        }

        return this.forbiddenWords.get(key) ?? this.applyMask(match, maskType);
      });
    }

    return result;
  }

  detect(text: string): string[] {
    if (this.forbiddenWords.size === 0) return [];

    if (this.enableRegex) {
      return this.detectWithRegex(text);
    } else {
      return this.detectWithWords(text);
    }
  }

  private detectWithWords(text: string): string[] {
    const pattern = this.wordBoundary 
      ? `\\b(${[...this.forbiddenWords.keys()]
          .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
          .join("|")})\\b`
      : `(${[...this.forbiddenWords.keys()]
          .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
          .join("|")})`;

    const regex = new RegExp(pattern, this.caseInsensitive ? "gi" : "g");
    const matches: string[] = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[0]);
    }

    return matches;
  }

  private detectWithRegex(text: string): string[] {
    const matches: string[] = [];
    
    for (const regex of this.regexPatterns.values()) {
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push(match[0]);
      }
    }

    return matches;
  }

  detectWithPositions(text: string): Array<{ word: string; start: number; end: number }> {
    if (this.forbiddenWords.size === 0) return [];

    const positions: Array<{ word: string; start: number; end: number }> = [];
    
    if (this.enableRegex) {
      for (const [key, regex] of this.regexPatterns) {
        let match;
        while ((match = regex.exec(text)) !== null) {
          positions.push({
            word: match[0],
            start: match.index,
            end: match.index + match[0].length
          });
        }
      }
    } else {
      const pattern = this.wordBoundary 
        ? `\\b(${[...this.forbiddenWords.keys()]
            .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
            .join("|")})\\b`
        : `(${[...this.forbiddenWords.keys()]
            .map((w) => w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
            .join("|")})`;

      const regex = new RegExp(pattern, this.caseInsensitive ? "gi" : "g");
      let match;
      
      while ((match = regex.exec(text)) !== null) {
        positions.push({
          word: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }

    return positions.sort((a, b) => a.start - b.start);
  }

  getDetectionLogs(): string[] {
    return [...this.detectionLogs];
  }

  getStats(): DetectionStats {
    return { ...this.detectionStats };
  }

  resetStats() {
    this.detectionStats = {
      totalDetections: 0,
      uniqueWords: [],
      detectionCounts: {}
    };
    this.detectionLogs = [];
  }

  setMaskChar(char: string) {
    this.maskChar = char;
  }

  setCaseInsensitive(value: boolean) {
    this.caseInsensitive = value;
  }

  setLogDetections(value: boolean) {
    this.logDetections = value;
  }

  setCustomReplacer(replacer: (word: string, context: string) => string) {
    this.customReplacer = replacer;
  }

  // Utility methods
  sanitizeText(text: string): string {
    return this.filter(text, "replace", "full");
  }

  isClean(text: string): boolean {
    return this.detect(text).length === 0;
  }

  getCleanPercentage(text: string): number {
    const detected = this.detect(text);
    const totalWords = text.split(/\s+/).length;
    return totalWords > 0 ? ((totalWords - detected.length) / totalWords) * 100 : 100;
  }
}

// Preset word lists
export const PRESET_WORDS = {
  profanity: [
    "badword", "offensive", "rude", "vulgar", "inappropriate",
    "curse", "swear", "expletive", "obscene", "lewd"
  ],
  spam: [
    "buy now", "click here", "free money", "make money fast",
    "weight loss", "viagra", "casino", "lottery", "winner"
  ],
  phishing: [
    "verify account", "update password", "security alert",
    "suspended account", "unusual activity", "login attempt"
  ]
};

// Utility functions
export function createWordSensor(config: WordSensorConfig = {}): WordSensor {
  return new WordSensor(config);
}

export function createProfanityFilter(maskChar = "*"): WordSensor {
  return new WordSensor({
    words: PRESET_WORDS.profanity,
    maskChar,
    caseInsensitive: true,
    logDetections: true
  });
}

export function createSpamFilter(maskChar = "#"): WordSensor {
  return new WordSensor({
    words: PRESET_WORDS.spam,
    maskChar,
    caseInsensitive: true,
    logDetections: true,
    wordBoundary: false
  });
}

export function createPhishingFilter(maskChar = "!"): WordSensor {
  return new WordSensor({
    words: PRESET_WORDS.phishing,
    maskChar,
    caseInsensitive: true,
    logDetections: true,
    wordBoundary: false
  });
}

export function getNestedValue(obj: any, path: string): any {
  return path
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export async function loadForbiddenWordsFromAPI(
  url: string,
  path: string | null,
  sensor: WordSensor
): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.statusText}`);
    }

    const data = await response.json();
    let words: string[] = [];

    if (Array.isArray(data)) {
      words = data;
    } else if (path) {
      words = getNestedValue(data, path) ?? [];
    }

    if (!Array.isArray(words)) {
      throw new Error("Invalid words format from API");
    }

    sensor.addWords(words);
    console.log("Forbidden words added from API:", words);
    return true;
  } catch (error) {
    console.error("Error loading forbidden words:", error);
    return false;
  }
}

export async function loadWordsFromFile(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const words = content
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0 && !line.startsWith('#'));
        resolve(words);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export function validateRegexPattern(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

export function escapeRegexSpecialChars(str: string): string {
  return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
}

export function createCustomReplacer(
  replacementMap: Record<string, string>
): (word: string, context: string) => string {
  return (word: string) => {
    const key = word.toLowerCase();
    return replacementMap[key] || word;
  };
}

export function createEmojiReplacer(): (word: string, context: string) => string {
  const emojiMap: Record<string, string> = {
    'badword': '🤬',
    'offensive': '😤',
    'rude': '😒',
    'vulgar': '🤢',
    'inappropriate': '😳',
    'curse': '💢',
    'swear': '😠',
    'expletive': '🤯',
    'obscene': '😱',
    'lewd': '😵'
  };
  
  return (word: string) => {
    const key = word.toLowerCase();
    return emojiMap[key] || '🤐';
  };
}

// Batch processing utilities
export function batchFilter(
  texts: string[],
  sensor: WordSensor,
  mode: "replace" | "remove" | "highlight" = "replace",
  maskType: "full" | "partial" | "smart" = "full"
): string[] {
  return texts.map(text => sensor.filter(text, mode, maskType));
}

export function batchDetect(
  texts: string[],
  sensor: WordSensor
): Array<{ text: string; detected: string[] }> {
  return texts.map(text => ({
    text,
    detected: sensor.detect(text)
  }));
}

export function getBatchStats(
  texts: string[],
  sensor: WordSensor
): {
  totalTexts: number;
  cleanTexts: number;
  dirtyTexts: number;
  totalDetections: number;
  averageCleanPercentage: number;
} {
  const results = texts.map(text => ({
    text,
    detected: sensor.detect(text),
    cleanPercentage: sensor.getCleanPercentage(text)
  }));

  const totalTexts = results.length;
  const cleanTexts = results.filter(r => r.detected.length === 0).length;
  const dirtyTexts = totalTexts - cleanTexts;
  const totalDetections = results.reduce((sum, r) => sum + r.detected.length, 0);
  const averageCleanPercentage = results.reduce((sum, r) => sum + r.cleanPercentage, 0) / totalTexts;

  return {
    totalTexts,
    cleanTexts,
    dirtyTexts,
    totalDetections,
    averageCleanPercentage
  };
}